/**
 * Header row for the medical PA form: patient identity, the regimen care-tag
 * badge, and the PA-form three-dots menu.
 *
 * @module medicalPaForm/components/medicalPaFormHeader
 */
import BadgeWithIconRounded from "../../../components/badgeWithIconRounded/badgeWithIconRounded";
import PaFormThreeDotsMenu from "../../../components/threeDotsWithClickableOptions/paFormThreeDotsMenu/PaFormThreeDotsMenu";
import { ScreenNamesForBigQuery } from "../../../enums/eventsData";
import { generateFullName } from "../../../utils/generateOrderDataForTable";
import { getBadgeDetailsAndIconColorForCareTag } from "../../singleOrderLayout/components/busniessOffice/utils/getBadgeAndIconColorForCareTag";
import { MedicalPaFormHeaderProps } from "../types";

/**
 * Renders the form header. The three-dots menu is only shown once the order,
 * auth-on-file, and submission payloads are all present.
 *
 * @param props - See {@link MedicalPaFormHeaderProps}.
 * @returns The header row element.
 */
const MedicalPaFormHeader = ({
  generalOrderDetails,
  authOnFileDetails,
  submissionData,
  guidelinesDocuments,
  healthCareFacilityData,
}: MedicalPaFormHeaderProps) => {
  const regimenTag =
    authOnFileDetails?.auth_on_file?.flowsheet_regimen_details?.regimen_tag ??
    "";
  const badgeDetails = getBadgeDetailsAndIconColorForCareTag(regimenTag);

  return (
    <div className="medical-pharma-pa-form__body--header mb-4">
      <div className="medical-pharma-pa-form__body--header-details flex items-center gap-2">
        <div className="medical-pharma-pa-form__body patient-name text-h16 font-semibold leading-6 text-primaryGray-1">
          {generateFullName(
            generalOrderDetails?.order?.demographics?.first_name ?? "",
            "",
            generalOrderDetails?.order?.demographics?.last_name ?? "",
          )}
        </div>
        <div className="medical-pharma-pa-form__body page-name text-sm font-semiBold text-black">
          (
          {generalOrderDetails?.order?.demographics?.patient_id ??
            generalOrderDetails?.order?.demographics?.patient_mrn ??
            ""}
          )
        </div>
        {regimenTag !== "" && (
          <BadgeWithIconRounded
            text={badgeDetails.badgeText}
            icon={
              <div
                style={{
                  width: "10px",
                  height: "10px",
                  background: badgeDetails.iconColor,
                  borderRadius: "9999px",
                }}
              ></div>
            }
            id="id2"
            bgColor={badgeDetails.badgeBgColor}
            textColor={badgeDetails.badgeTextColor}
            borderColor={badgeDetails.borderColor}
          />
        )}

        <div className="ml-auto flex items-center justify-end gap-2">
          {generalOrderDetails?.order &&
            authOnFileDetails?.auth_on_file &&
            submissionData && (
              <PaFormThreeDotsMenu
                items={guidelinesDocuments ?? []}
                healthCareFacilityData={healthCareFacilityData}
                authOnFile={authOnFileDetails.auth_on_file}
                generalOrderDetails={generalOrderDetails.order}
                submissionData={submissionData.submission}
                screenName={ScreenNamesForBigQuery.FORM_SCREEN}
              />
            )}
        </div>
      </div>
    </div>
  );
};

export default MedicalPaFormHeader;
